import React from "react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { ExternalLink, Pencil } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Column, DataTable } from "@/components/ui/data-table";
import { PresignedImageWithPlaceholder } from "@/components/common/PresignedImageWithPlaceholder";
import { useAuthenticatedUser } from "@/hooks/useAuthenticatedUser";
import { cn } from "@/lib/utils";
import type { GuesteriaPartnerRevenueRow } from "../guesteriaPartnerRevenueModel";

type GuesteriaPartnersRevenueTableProps = {
  data: GuesteriaPartnerRevenueRow[];
  loading: boolean;
  currentPage: number;
  totalPages: number;
  sortField: string | null;
  sortDirection: "asc" | "desc" | null;
  onSort: (field: string) => void;
  onPageChange: (page: number) => void;
};

function getCurrencySymbol(currency: string): string {
  if (currency === "EUR") return "€";
  if (currency === "USD") return "$";
  if (currency === "GBP") return "£";
  return `${currency} `;
}

export const GuesteriaPartnersRevenueTable = ({
  data,
  loading,
  currentPage,
  totalPages,
  sortField,
  sortDirection,
  onSort,
  onPageChange,
}: GuesteriaPartnersRevenueTableProps): React.JSX.Element => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  // GST sim fix, 2026-09-30: `GET /files/:id` needs `x-tenant-id` to resolve
  // a file's region — same fix as `UsersTable.tsx` (commit e591d62e). Without
  // it the hotel logo request 403s.
  const { userProfile } = useAuthenticatedUser();
  const tenantId = userProfile?.tenantId;

  const columns: Column<GuesteriaPartnerRevenueRow>[] = React.useMemo(
    () => [
      {
        key: "hotelLogoLetter",
        header: t("billing.guesteriaPartnersRevenue.logo"),
        className: "w-[72px] text-center",
        render: (_, row) => (
          <div className="flex justify-center">
            <PresignedImageWithPlaceholder
              className="h-10 w-10 rounded-lg border border-zinc-200 object-cover"
              photo={row.hotelLogoId}
              tenantId={tenantId}
              alt={row.hotelBrand}
              placeholder={
                <div
                  className={cn(
                    "flex h-10 w-10 items-center justify-center rounded-lg border border-zinc-200 font-serif text-lg font-semibold text-amber-300",
                    row.hotelLogoClassName,
                  )}
                >
                  {row.hotelLogoLetter}
                </div>
              }
            />
          </div>
        ),
      },
      {
        key: "hotelBrand",
        header: t("billing.guesteriaPartnersRevenue.hotelBrand"),
        className: "text-center",
        sortable: true,
        render: (value) => (
          <span className="inline-flex h-fit w-fit items-center justify-center text-center text-[10px] leading-[100%] font-normal tracking-[0] text-[#0A0A0A]">
            {String(value)}
          </span>
        ),
      },
      {
        key: "adminName",
        header: t("billing.guesteriaPartnersRevenue.admin"),
        className: "text-center",
        sortable: true,
        render: (_, row) => (
          <div className="flex items-center justify-start gap-2">
            {row.adminAvatarUrl ? (
              <img
                src={row.adminAvatarUrl}
                alt=""
                className="h-8 w-8 rounded-full object-cover"
              />
            ) : (
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-zinc-200 text-[10px] font-semibold text-zinc-700">
                {row.adminInitials}
              </div>
            )}
            <span className="inline-flex h-fit w-fit items-center justify-center text-[10px] leading-[100%] font-semibold tracking-[0] text-[#0A0A0A]">
              {row.adminName}
            </span>
          </div>
        ),
      },
      {
        key: "partnerName",
        header: t("billing.guesteriaPartnersRevenue.partner"),
        className: "text-center",
        render: (_, row) => (
          <div className="flex flex-col items-center gap-1">
            <span className="flex h-8 w-8 items-center justify-center rounded-md bg-slate-600 font-serif text-sm font-semibold text-white">
              G
            </span>
            <span className="inline-flex h-fit w-full items-center justify-center text-center text-[10px] leading-[100%] font-normal tracking-[0] text-[#0A0A0A]">
              {row.partnerName}
            </span>
          </div>
        ),
      },
      {
        key: "revenueEur",
        header: t("billing.guesteriaPartnersRevenue.revenue"),
        className: "text-center",
        sortable: true,
        render: (value) => (
          <span className="inline-flex h-fit w-fit items-center justify-center text-center text-[10px] leading-[100%] font-normal tracking-[0] text-[#0A0A0A]">
            {getCurrencySymbol("EUR")}
            {Number(value).toLocaleString("en-GB")}
          </span>
        ),
      },
      {
        key: "pms",
        header: t("billing.guesteriaPartnersRevenue.pms"),
        className: "text-center",
        sortable: true,
        render: (value) => (
          <span className="inline-flex h-fit w-fit items-center justify-center text-[10px] leading-4 font-normal tracking-[0] text-[#0A0A0A]">
            {String(value)}
          </span>
        ),
      },
      {
        key: "billingPlan",
        header: t("billing.guesteriaPartnersRevenue.billingPlan"),
        className: "text-center",
        sortable: true,
        render: (value) => (
          <span className="inline-flex items-center justify-center gap-1.5 text-sm text-zinc-900">
            <ExternalLink className="size-3 shrink-0 text-zinc-500" />
            {String(value)}
          </span>
        ),
      },
      {
        key: "actions",
        header: t("billing.guesteriaPartnersRevenue.actions"),
        className: "w-[72px] text-center",
        render: (_, row) => (
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-8 w-8"
            aria-label={t("billing.guesteriaPartnersRevenue.actions")}
            data-no-row-click="true"
            onClick={() =>
              navigate(`/guesteria-partners-revenue/${row.id}`, {
                state: { row },
              })
            }
          >
            <Pencil className="size-4 text-zinc-600" />
          </Button>
        ),
      },
    ],
    [navigate, t, tenantId],
  );

  return (
    <DataTable<GuesteriaPartnerRevenueRow>
      data={data}
      getRowKey={(row) => row.id}
      columns={columns}
      loading={loading}
      columnHeaderClassName="inline-flex h-fit w-fit items-center justify-center text-[10px] font-medium leading-5 tracking-[0] text-[#737373]"
      pagination={{
        currentPage,
        totalPages,
        onPageChange,
      }}
      sorting={{
        field: sortField,
        direction: sortDirection,
        onSort,
      }}
      emptyMessage={t("billing.guesteriaPartnersRevenue.empty")}
    />
  );
};
